import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  Users, 
  Award, 
  Clock, 
  DollarSign,
  ChevronLeft,
  ChevronRight,
  Play,
  X,
  ExternalLink,
  Download,
  Share2
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Mock project data - replace with actual data source
const mockProjectDetails = {
  1: {
    id: 1,
    title: "Smart Grid Infrastructure",
    description: "Advanced power distribution system with IoT integration for enhanced efficiency and monitoring capabilities. This comprehensive project revolutionizes the traditional power grid by implementing cutting-edge technology solutions that enable real-time monitoring, predictive maintenance, and optimal energy distribution.",
    category: "Power Systems",
    location: "Dubai, UAE",
    date: "2024",
    status: "Completed",
    team: "15 Engineers",
    duration: "18 months",
    budget: "$2.5M",
    client: "Dubai Electricity & Water Authority (DEWA)",
    projectManager: "Ahmed Al-Rashid",
    tags: ["IoT", "Smart Grid", "Power Distribution", "Monitoring", "Efficiency"],
    gallery: [
      { type: 'image', url: '/placeholder-image.svg', caption: 'Main control center overview' },
      { type: 'image', url: '/placeholder-image.svg', caption: 'Smart meters installation' },
      { type: 'image', url: '/placeholder-image.svg', caption: 'Network infrastructure' },
      { type: 'video', url: '/placeholder-image.svg', caption: 'System demonstration video' },
      { type: 'image', url: '/placeholder-image.svg', caption: 'Monitoring dashboard' },
      { type: 'image', url: '/placeholder-image.svg', caption: 'Field equipment' }
    ],
    specifications: {
      "Technical Specifications": {
        "Grid Capacity": "500 MW",
        "Smart Meters": "10,000 units",
        "Communication Protocol": "IEC 61850",
        "Data Processing": "Real-time analytics",
        "Monitoring Points": "2,500 sensors"
      },
      "Performance Metrics": {
        "Efficiency Improvement": "25%",
        "Downtime Reduction": "40%",
        "Energy Savings": "15%",
        "Response Time": "< 2 seconds",
        "Reliability": "99.9%"
      },
      "Environmental Impact": {
        "CO2 Reduction": "1,200 tons/year",
        "Energy Efficiency": "Grade A+",
        "Renewable Integration": "Solar & Wind ready",
        "Sustainability Rating": "LEED Gold",
        "Waste Reduction": "30%"
      }
    },
    challenges: [
      "Integration with legacy systems while maintaining 24/7 operations",
      "Implementing cybersecurity measures for IoT devices",
      "Training personnel on new monitoring systems",
      "Ensuring compliance with international standards"
    ],
    solutions: [
      "Phased implementation approach with redundant systems",
      "Multi-layered security architecture with encryption",
      "Comprehensive training program and documentation",
      "Regular audits and compliance verification"
    ],
    outcomes: [
      "25% improvement in grid efficiency",
      "40% reduction in maintenance costs",
      "Real-time fault detection and isolation",
      "Enhanced customer satisfaction scores"
    ]
  }
};

const relatedProjects = [
  {
    id: 2,
    title: "Telecommunications Network Expansion",
    category: "Telecommunications",
    thumbnail: "/placeholder-image.svg",
    status: "In Progress"
  },
  {
    id: 5,
    title: "Industrial Automation Suite",
    category: "Industrial",
    thumbnail: "/placeholder-image.svg",
    status: "Completed"
  },
  {
    id: 6,
    title: "Data Center Infrastructure",
    category: "Data Centers",
    thumbnail: "/placeholder-image.svg",
    status: "In Progress"
  }
];

const ProjectDetails = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const [project, setProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    // Simulate API call
    const projectData = mockProjectDetails[id];
    setProject(projectData);
  }, [id]);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-global-green"></div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800';
      case 'Planning':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % project.gallery.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + project.gallery.length) % project.gallery.length);
  };

  const GalleryModal = () => (
    <AnimatePresence>
      {isGalleryOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
          onClick={() => setIsGalleryOpen(false)}
        >
          <div className="relative max-w-6xl max-h-[90vh] mx-4">
            <button
              onClick={() => setIsGalleryOpen(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <X className="w-8 h-8" />
            </button>
            
            <div className="relative">
              <img
                src={project.gallery[currentImageIndex].url}
                alt={project.gallery[currentImageIndex].caption}
                className="max-w-full max-h-[80vh] object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </div>
            
            <div className="text-center mt-4 text-white">
              <p className="text-lg">{project.gallery[currentImageIndex].caption}</p>
              <p className="text-sm text-gray-300 mt-2">
                {currentImageIndex + 1} of {project.gallery.length}
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-global-green to-global-light-blue text-white">
        <div className="absolute inset-0 bg-black/10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-40">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Back button */}
            <Link
              to="/projects"
              className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors duration-300"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Projects
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h1 className="text-4xl md:text-5xl font-bold mb-4">
                  {project.title}
                </h1>
                <p className="text-xl text-white/90 mb-6">
                  {project.description}
                </p>
                
                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {project.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-white/20 text-white px-3 py-1 rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Project Info Card */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-white/80">Status</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(project.status)}`}>
                      {project.status}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-white/80">Location</span>
                    <span className="text-white font-medium">{project.location}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-white/80">Duration</span>
                    <span className="text-white font-medium">{project.duration}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-white/80">Team Size</span>
                    <span className="text-white font-medium">{project.team}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-white/80">Budget</span>
                    <span className="text-white font-medium">{project.budget}</span>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="mt-6 space-y-3">
                  <button className="w-full bg-white text-global-green px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300 flex items-center justify-center">
                    <Download className="w-4 h-4 mr-2" />
                    Download Report
                  </button>
                  <button className="w-full border border-white/30 text-white px-4 py-2 rounded-lg font-semibold hover:bg-white/10 transition-colors duration-300 flex items-center justify-center">
                    <Share2 className="w-4 h-4 mr-2" />
                    Share Project
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Navigation Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg p-2 mb-8"
        >
          <div className="flex space-x-1">
            {['overview', 'gallery', 'specifications', 'timeline'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all duration-300 ${
                  activeTab === tab
                    ? 'bg-gradient-to-r from-global-green to-global-light-blue text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-8"
            >
              {/* Challenges */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Challenges</h3>
                <div className="space-y-4">
                  {project.challenges.map((challenge, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0" />
                      <p className="text-gray-700">{challenge}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Solutions */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Solutions</h3>
                <div className="space-y-4">
                  {project.solutions.map((solution, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-global-green rounded-full mt-2 flex-shrink-0" />
                      <p className="text-gray-700">{solution}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Outcomes */}
              <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Key Outcomes</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {project.outcomes.map((outcome, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-global-light-blue rounded-full mt-2 flex-shrink-0" />
                      <p className="text-gray-700">{outcome}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'gallery' && (
            <motion.div
              key="gallery"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Project Gallery</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {project.gallery.map((item, index) => (
                  <div
                    key={index}
                    className="relative group cursor-pointer rounded-xl overflow-hidden"
                    onClick={() => {
                      setCurrentImageIndex(index);
                      setIsGalleryOpen(true);
                    }}
                  >
                    <img
                      src={item.url}
                      alt={item.caption}
                      className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      {item.type === 'video' ? (
                        <Play className="w-12 h-12 text-white" />
                      ) : (
                        <ExternalLink className="w-8 h-8 text-white" />
                      )}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                      <p className="text-white text-sm">{item.caption}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {activeTab === 'specifications' && (
            <motion.div
              key="specifications"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="space-y-8"
            >
              {Object.entries(project.specifications).map(([category, specs]) => (
                <div key={category} className="bg-white rounded-2xl shadow-lg p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">{category}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {Object.entries(specs).map(([key, value]) => (
                      <div key={key} className="flex justify-between items-center py-3 border-b border-gray-100 last:border-b-0">
                        <span className="text-gray-600 font-medium">{key}</span>
                        <span className="text-gray-900 font-semibold">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          {activeTab === 'timeline' && (
            <motion.div
              key="timeline"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Project Timeline</h3>
              <div className="space-y-6">
                {[
                  { phase: "Planning & Design", duration: "3 months", status: "completed" },
                  { phase: "Infrastructure Setup", duration: "6 months", status: "completed" },
                  { phase: "System Integration", duration: "4 months", status: "completed" },
                  { phase: "Testing & Optimization", duration: "3 months", status: "completed" },
                  { phase: "Deployment & Training", duration: "2 months", status: "completed" }
                ].map((phase, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <div className={`w-4 h-4 rounded-full ${
                      phase.status === 'completed' ? 'bg-global-green' : 'bg-gray-300'
                    }`} />
                    <div className="flex-grow">
                      <h4 className="font-semibold text-gray-900">{phase.phase}</h4>
                      <p className="text-gray-600">{phase.duration}</p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      phase.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {phase.status === 'completed' ? 'Completed' : 'Pending'}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Related Projects */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16"
        >
          <h3 className="text-3xl font-bold text-gray-900 mb-8">Related Projects</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {relatedProjects.map((relatedProject) => (
              <Link
                key={relatedProject.id}
                to={`/projects/${relatedProject.id}`}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 overflow-hidden"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={relatedProject.thumbnail}
                    alt={relatedProject.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
                <div className="p-6">
                  <div className="mb-3">
                    <span className="text-sm font-medium text-global-green bg-global-green/10 px-3 py-1 rounded-full">
                      {relatedProject.category}
                    </span>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 group-hover:text-global-green transition-colors duration-300">
                    {relatedProject.title}
                  </h4>
                </div>
              </Link>
            ))}
          </div>
        </motion.div>
      </div>

      <GalleryModal />
    </div>
  );
};

export default ProjectDetails;